import { BodyText, Headline, Spinner } from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, Fragment, ReactNode, useEffect, useMemo } from 'react';
import { useClientRect } from '../../hooks/use-client-rect';
import { ContentStore } from '../../stores/content.store';
import { PortalMode } from '../../stores/portal.store';
import { modeObserver } from '../mode-observer';
import * as Styles from './content.module.less';

interface ContentBlockPropsStrict {
    className?: string;
    contentClassName?: string;
    white?: boolean;
    centered?: boolean;
    children: ReactNode;
}

export interface ContentBlockProps extends ContentBlockPropsStrict {
    /** Unstrict Props */
    [propName: string]: any;
}

export const ContentBlock: FC<ContentBlockProps> = ({
    centered,
    children,
    className,
    contentClassName,
    white,
    ...props
}) => (
    <div
        className={classNames(
            Styles.contentBlockWrapper,
            {
                'bg-white': white,
                'flex-grow-1 flex-basis-0 d-f flex-column justify-content-center': centered,
            },
            className
        )}
        {...props}
    >
        <div className={classNames(Styles.contentBlock, contentClassName)}>{children}</div>
    </div>
);

interface ContentHeaderProps {
    /** Unstrict Props */
    [propName: string]: any;
    className?: string;
    title?: string;
}

export const ContentHeader: FC<ContentHeaderProps> = ({ className, title, ...props }) => (
    <div className={classNames(Styles.contentBlockTitle, className)} {...props}>
        {title}
    </div>
);

interface ContentHeaderHeadlineProps {
    /** Unstrict Props */
    [propName: string]: any;
    className?: string;
    children: ReactNode;
}

export const ContentHeaderHeadline: FC<ContentHeaderHeadlineProps> = ({
    children,
    className,
    ...props
}) => (
    <Headline size="large" className={classNames('m-b-0', className)} {...props}>
        {children}
    </Headline>
);

const LayoutFooter: FC = observer(() => {
    const [{ footer }] = useDependencies(ContentStore);
    const [rect, ref] = useClientRect();
    const [rect2, ref2] = useClientRect();
    const FooterComponent = footer?.comp;

    /**
     * Sticky position doesn't work in shadow dom (MFE) when scrolling is performed in parent document
     * and we can't move scrolling to MFE because in this case it work incorrectly in mobile browsers
     */
    return FooterComponent ? (
        <Fragment>
            <div
                className={Styles.footer}
                ref={ref}
                style={rect2?.left ? { left: `${rect2.left}px` } : undefined}
            >
                <FooterComponent />
            </div>
            <div ref={ref2} style={{ height: `${rect?.height ?? 0}px` }} />
        </Fragment>
    ) : null;
});

interface ContentFooterProps {
    component?: FC;
    thin?: boolean;
    className?: string;
    children?: ReactNode;
}

export const ContentFooter: FC<ContentFooterProps> = ({ children, className, thin }) => {
    const [{ registerFooter, unregisterFooter }] = useDependencies(ContentStore);
    const id = useMemo(() => `${Date.now()}_${Math.round(Math.random() * 10000)}`, []);

    useEffect(() => {
        registerFooter(
            id,
            children
                ? () => (
                      <div
                          className={classNames(className, {
                              [Styles.contentFooter]: !thin,
                          })}
                      >
                          {children}
                      </div>
                  )
                : undefined
        );
    }, [id, children, registerFooter, className, thin]);

    useEffect(() => () => unregisterFooter(id), [id, unregisterFooter]);

    return null;
};

interface ContentPagePropsStrict {
    mode: PortalMode;
    thin?: boolean;
    white?: boolean;
    loading?: boolean;
    children?: ReactNode;
    className?: string;
}

export interface ContentPageProps extends ContentPagePropsStrict {
    /** Unstrict Props */
    [propName: string]: any;
}

export const ContentPage: FC<ContentPageProps> = provide({ singletons: [ContentStore] })(
    ({ children, className, loading, mode, thin, white, ...props }) => (
        <div
            className={classNames(
                'min-h-100',
                Styles.contentPage,
                {
                    'bg-white': !!white,
                    'd-f flex-column justify-content-center align-items-center': !!loading,
                    [Styles.contentPageMobile]: mode.mobile,
                    [Styles.contentPageTablet]: mode.tablet,
                    [Styles.contentPageDesktop]: mode.desktop,
                    [Styles.contentPageThin]: !!thin,
                },
                className
            )}
            {...props}
        >
            <div className="d-f flex-column">
                {loading ? (
                    <Fragment>
                        <Spinner className="m-t-3" />
                        <BodyText subdued>Loading</BodyText>
                    </Fragment>
                ) : (
                    children
                )}
            </div>

            <LayoutFooter />
            {!mode.desktop && (
                <div className={Styles.footerSt}>
                    <BodyText>powered by</BodyText>
                    <img
                        src="https://static.servicetitan.com/portal/st-logo-white.svg"
                        alt="ServiceTitan"
                    />
                </div>
            )}
        </div>
    )
);

export const ContentPageObserver = modeObserver(ContentPage);

interface ContentCardProps {
    /** Unstrict Props */
    [propName: string]: any;
    title?: string;
    description?: string;
    className?: string;
    children?: ReactNode;
}

export const ContentCard: FC<ContentCardProps> = ({
    children,
    className,
    description,
    title,
    ...props
}) => {
    return (
        <div className={classNames(Styles.contentCard, className)} {...props}>
            {!!title && <Headline className={Styles.contentCardHeader}>{title}</Headline>}
            {!!description && (
                <BodyText className={Styles.contentCardDescription}>{description}</BodyText>
            )}
            {children}
        </div>
    );
};
